import React, { useEffect, useRef, useState } from 'react'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import { NavLink } from 'react-router-dom'
import { formatTitle } from '../../utils/formatTitle'
import { useTranslation } from 'react-i18next'
import Select, { StylesConfig } from 'react-select'
import { CircularProgress } from '@mui/material'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'

interface EntitySelectorProps<T = any> {
  value: string | null | undefined
  linkTo: string
  rowData: T
  options: Array<{
    _id?: string
    [key: string]: any
  }>
  optionLabelKey: string
  optionValueKey?: string
  placeholder: string
  entityName: string
  onSelect: (selectedId: string, rowData: T) => Promise<void> | void
  /** Fired when the menu opens so options stay scoped to this row. */
  onFetchOptions?: (rowData: T) => Promise<void> | void
  isLoading?: boolean
  disabled?: boolean
}

/** Compact table-cell variant of shared INCYIA select styles. */
const tableSelectStyles: StylesConfig<any, false> = {
  ...incyiaSelectStyles,
  control: (provided, state) => ({
    ...incyiaSelectStyles.control?.(provided, state),
    minHeight: 32,
    height: 32,
    fontSize: 16,
  }),
  singleValue: (provided) => ({
    ...incyiaSelectStyles.singleValue?.(provided, { isDisabled: false } as any),
    fontSize: 16,
  }),
  placeholder: (provided) => ({
    ...incyiaSelectStyles.placeholder?.(provided, { isDisabled: false } as any),
    fontSize: 16,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px',
    height: 30,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: 30,
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '0 6px',
    color: '#8A9BA7',
  }),
  loadingIndicator: (provided) => ({
    ...provided,
    color: '#0D9B87',
    padding: '0 4px',
  }),
}

const EntitySelector = <T,>({
  value,
  linkTo,
  rowData,
  options,
  optionLabelKey,
  optionValueKey = '_id',
  placeholder,
  entityName,
  onSelect,
  onFetchOptions,
  isLoading = false,
  disabled = false,
}: EntitySelectorProps<T>) => {
  const menu = useExclusiveMenu()
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement | null>(null)

  const handleOpen = () => {
    menu.onMenuOpen()
    // Do not await — awaiting + disabling the control closed the menu.
    if (onFetchOptions) {
      void onFetchOptions(rowData)
    }
  }

  const handleChange = async (selectedOption: any) => {
    if (selectedOption?.value) {
      try {
        await onSelect(selectedOption.value, rowData)
      } catch (error) {
        console.error('Error updating entity:', error)
      }
    }
  }

  if (value) {
    return (
      <NavLink
        className="entity-selector-link"
        to={linkTo}
        onClick={(e) => e.stopPropagation()}
        title={formatTitle(value)}
      >
        {formatTitle(value)}
      </NavLink>
    )
  }

  const selectOptions = options
    .filter((opt) => Boolean(opt[optionValueKey]))
    .map((opt) => ({
      value: String(opt[optionValueKey]),
      label: formatTitle(opt[optionLabelKey] || '') || '—',
    }))

  return (
    <div
      ref={containerRef}
      className="entity-selector"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Select
        options={selectOptions}
        onChange={handleChange}
        placeholder={placeholder}
        styles={tableSelectStyles}
        isLoading={isLoading}
        isDisabled={disabled}
        {...menu}
        onMenuOpen={handleOpen}
        onMenuClose={menu.onMenuClose}
        classNamePrefix="entity-select"
        aria-label={t('entitySelector.selectEntity', {
          entity: entityName,
          defaultValue: `Select ${entityName}`,
        })}
        noOptionsMessage={() =>
          isLoading
            ? t('entitySelector.loading', 'Loading...')
            : t('entitySelector.noResults', 'No results found')
        }
        loadingMessage={() => (
          <span className="entity-selector-loading">
            <CircularProgress size={12} sx={{ color: '#0d9b87' }} />
            {t('entitySelector.loading', 'Loading...')}
          </span>
        )}
      />
    </div>
  )
}

export default EntitySelector
